import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';

import { SHRED_COLORS, SHRED_COPY, SHRED_FONTS, SHRED_STATUS } from '../constants';
import {
  formatCountdown,
  formatDateRange,
  getCountdownTarget,
  getCurrentDay,
  getEffectiveStatus,
} from '../helpers';
import { useNow } from '../hooks';
import { focusRing } from './shared';

const EMBERS = Array.from({ length: 18 }, (_, i) => ({
  left: `${(i * 37 + 7) % 100}%`,
  size: 3 + ((i * 5) % 6),
  delay: `${(i * 0.7) % 7}s`,
  duration: `${6 + ((i * 3) % 6)}s`,
}));

const STATUS_LABELS = {
  [SHRED_STATUS.UPCOMING]: SHRED_COPY.STATUS_UPCOMING,
  [SHRED_STATUS.LIVE]: SHRED_COPY.STATUS_LIVE,
  [SHRED_STATUS.ENDED]: SHRED_COPY.STATUS_ENDED,
};

const CountdownDigit = ({ value, unit }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box
      sx={{
        minWidth: { xs: 62, sm: 84 },
        px: 1,
        py: { xs: 1, sm: 1.5 },
        borderRadius: '10px',
        background: 'linear-gradient(180deg, #2c1d16 0%, #160f0c 100%)',
        border: `1px solid ${SHRED_COLORS.line}`,
        boxShadow: 'inset 0 -6px 12px rgba(0,0,0,0.5), 0 0 24px rgba(255,90,31,0.14)',
        fontFamily: SHRED_FONTS.numeric,
        fontWeight: 500,
        fontSize: { xs: 32, sm: 46 },
        lineHeight: 1,
        color: SHRED_COLORS.gold,
        textShadow: '0 0 14px rgba(255,138,31,0.6)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {String(value).padStart(2, '0')}
    </Box>
    <Typography
      sx={{ mt: 0.5, fontFamily: SHRED_FONTS.numeric, fontSize: 10, letterSpacing: '0.16em', color: SHRED_COLORS.muted }}
    >
      {unit}
    </Typography>
  </Box>
);

const DayPips = ({ day, dayCount, replaying }) => (
  <Box
    role="img"
    aria-label={`Day ${day} of ${dayCount}`}
    sx={{ display: 'flex', gap: { xs: '4px', sm: '6px' }, justifyContent: 'center', mt: 1.5 }}
  >
    {Array.from({ length: dayCount }, (_, i) => {
      const lit = i < day;
      const today = i + 1 === day;
      return (
        <Box
          key={i}
          className="shred-motion"
          sx={{
            width: { xs: 18, sm: 26 },
            height: 8,
            borderRadius: '4px',
            background: lit ? SHRED_COLORS.heat : 'rgba(255,255,255,0.12)',
            boxShadow: today && !replaying ? `0 0 10px ${SHRED_COLORS.flame}` : 'none',
            transition: 'background 300ms ease',
          }}
        />
      );
    })}
  </Box>
);

const ShareButton = ({ onNotice, shareText }) => {
  const [busy, setBusy] = useState(false);
  const handleShare = async () => {
    if (busy) return;
    setBusy(true);
    const url = window.location.origin + window.location.pathname;
    try {
      if (navigator.share) {
        await navigator.share({ title: SHRED_COPY.SHARE_TITLE, text: shareText || SHRED_COPY.SHARE_TEXT, url });
      } else {
        await navigator.clipboard.writeText(url);
        onNotice(SHRED_COPY.SHARE_COPIED);
      }
    } catch (error) {
      // AbortError means the person closed the share sheet; anything else falls back to copy.
      if (error?.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url);
          onNotice(SHRED_COPY.SHARE_COPIED);
        } catch (copyError) {
          onNotice(SHRED_COPY.SHARE_FAILED);
        }
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      onClick={handleShare}
      startIcon={<IosShareIcon />}
      sx={{
        ...focusRing,
        color: SHRED_COLORS.text,
        border: `1px solid ${SHRED_COLORS.line}`,
        borderRadius: '999px',
        px: 2.5,
        fontFamily: SHRED_FONTS.body,
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': { background: 'rgba(255,255,255,0.06)' },
      }}
    >
      {SHRED_COPY.SHARE_LABEL}
    </Button>
  );
};

const Hero = ({ challenge, skewMs, unavailable, loading, displayDay, replaying, onNotice, shareText }) => {
  const nowMs = useNow(1000, skewMs);
  const status = getEffectiveStatus(challenge, nowMs);
  const countdown = unavailable || loading ? null : formatCountdown(getCountdownTarget(challenge, nowMs), nowMs);
  const day = replaying ? displayDay : getCurrentDay(challenge, nowMs);
  const dateRange = formatDateRange(challenge.startMs, challenge.endMs, challenge.timezone);
  const live = status === SHRED_STATUS.LIVE;

  return (
    <Box
      component="header"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'center',
        px: 2,
        pt: { xs: 5, md: 8 },
        pb: { xs: 5, md: 7 },
        background: `radial-gradient(ellipse 90% 70% at 50% 110%, rgba(255,59,48,0.55), transparent 62%),
          radial-gradient(ellipse 60% 50% at 50% 100%, rgba(255,194,51,0.28), transparent 70%),
          ${SHRED_COLORS.ink}`,
      }}
    >
      {EMBERS.map((ember, i) => (
        <Box
          key={i}
          aria-hidden="true"
          className="shred-motion"
          sx={{
            position: 'absolute',
            bottom: -12,
            left: ember.left,
            width: ember.size,
            height: ember.size,
            borderRadius: '50%',
            background: i % 3 === 0 ? SHRED_COLORS.gold : SHRED_COLORS.flame,
            boxShadow: `0 0 8px ${SHRED_COLORS.flame}`,
            opacity: 0,
            animation: `shredEmber ${ember.duration} ease-in ${ember.delay} infinite`,
          }}
        />
      ))}

      <Box sx={{ position: 'relative' }}>
        <Box
          className={live ? 'shred-motion' : undefined}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 0.6,
            borderRadius: '999px',
            border: `1px solid ${live ? SHRED_COLORS.flame : SHRED_COLORS.line}`,
            background: live ? 'rgba(255,90,31,0.16)' : 'rgba(255,255,255,0.05)',
            animation: live ? 'shredPulse 2.2s ease-in-out infinite' : 'none',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: live ? SHRED_COLORS.red : SHRED_COLORS.muted,
            }}
          />
          <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 12, letterSpacing: '0.18em', color: SHRED_COLORS.text }}>
            {unavailable ? SHRED_COPY.STATUS_OFFLINE : loading ? SHRED_COPY.STATUS_LOADING : STATUS_LABELS[status]}
          </Typography>
        </Box>

        <Typography
          sx={{
            mt: 2,
            fontFamily: SHRED_FONTS.numeric,
            fontSize: { xs: 11, sm: 13 },
            letterSpacing: '0.22em',
            color: SHRED_COLORS.flame,
          }}
        >
          {SHRED_COPY.KICKER}
        </Typography>

        <Typography
          component="h1"
          aria-label={`${SHRED_COPY.TITLE_TOP} ${SHRED_COPY.TITLE_BOTTOM}`}
          sx={{
            fontFamily: SHRED_FONTS.display,
            fontWeight: 800,
            textTransform: 'uppercase',
            lineHeight: 0.84,
            mt: 1,
            fontSize: { xs: 'clamp(48px, 17vw, 120px)', md: 150 },
          }}
        >
          <Box component="span" aria-hidden="true" sx={{ display: 'block', color: SHRED_COLORS.text, fontSize: '0.46em', letterSpacing: '0.08em' }}>
            {SHRED_COPY.TITLE_TOP}
          </Box>
          <Box
            component="span"
            aria-hidden="true"
            className="shred-motion"
            sx={{
              display: 'block',
              background: 'linear-gradient(180deg, #ffe28a 0%, #ff8a1f 45%, #ff3b30 100%)',
              backgroundSize: '100% 200%',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              filter: 'drop-shadow(0 0 22px rgba(255,90,31,0.45))',
              animation: 'shredFlicker 3.4s ease-in-out infinite',
            }}
          >
            {SHRED_COPY.TITLE_BOTTOM}
          </Box>
        </Typography>

        <Typography
          sx={{ mt: 2, mx: 'auto', maxWidth: 520, color: SHRED_COLORS.muted, fontFamily: SHRED_FONTS.body, fontSize: { xs: 15, md: 17 } }}
        >
          {SHRED_COPY.TAGLINE}
        </Typography>

        <Box sx={{ mt: 3.5 }}>
          {unavailable ? (
            <Typography role="status" sx={{ fontFamily: SHRED_FONTS.body, fontSize: 15, color: SHRED_COLORS.gold }}>
              {SHRED_COPY.LOAD_ERROR}
            </Typography>
          ) : loading ? (
            <Typography role="status" sx={{ fontFamily: SHRED_FONTS.body, fontSize: 15, color: SHRED_COLORS.muted }}>
              {SHRED_COPY.LOADING_STANDINGS}
            </Typography>
          ) : status === SHRED_STATUS.ENDED ? (
            <Typography
              sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 700, fontSize: { xs: 34, sm: 44 }, textTransform: 'uppercase', color: SHRED_COLORS.gold }}
            >
              {SHRED_COPY.COUNTDOWN_ENDED}
            </Typography>
          ) : (
            <>
              <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 12, letterSpacing: '0.2em', color: SHRED_COLORS.muted, mb: 1 }}>
                {(live ? SHRED_COPY.COUNTDOWN_LIVE : SHRED_COPY.COUNTDOWN_UPCOMING).toUpperCase()}
              </Typography>
              {countdown && (
                <Box
                  role="timer"
                  aria-label={`${countdown.days} days ${countdown.hours} hours ${countdown.minutes} minutes`}
                  sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, sm: 1.5 } }}
                >
                  <CountdownDigit value={countdown.days} unit="DAYS" />
                  <CountdownDigit value={countdown.hours} unit="HRS" />
                  <CountdownDigit value={countdown.minutes} unit="MIN" />
                  <CountdownDigit value={countdown.seconds} unit="SEC" />
                </Box>
              )}
            </>
          )}
        </Box>

        {day > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography
              sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 700, fontSize: { xs: 26, sm: 32 }, textTransform: 'uppercase', color: SHRED_COLORS.text }}
            >
              Day {day} of {challenge.dayCount}
              {replaying && (
                <Box component="span" sx={{ ml: 1, fontSize: 14, color: SHRED_COLORS.gold, fontFamily: SHRED_FONTS.numeric }}>
                  REPLAY
                </Box>
              )}
            </Typography>
            <DayPips day={day} dayCount={challenge.dayCount} replaying={replaying} />
          </Box>
        )}

        <Box sx={{ mt: 3.5, display: 'flex', gap: 1.5, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
          {dateRange && (
            <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 13, color: SHRED_COLORS.muted }}>{dateRange}</Typography>
          )}
          <ShareButton onNotice={onNotice} shareText={shareText} />
        </Box>
      </Box>
    </Box>
  );
};

export default Hero;
